import React, { useState, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Heading,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Textarea,
  Switch,
  Icon,
  Radio,
  RadioGroup,
  Stack,
  Spinner,
  Center,
  Divider,
  Flex,
} from '@chakra-ui/react';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiPlay, FiPause, FiX, FiDownload, FiUpload, FiClock, FiUser, FiCalendar, FiFileText, FiSave, FiPhone } from 'react-icons/fi';
import { useDropzone } from 'react-dropzone';

const BatchCall = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Theme colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.700', 'white');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');

  // Dummy data for batch calls
  const [batches, setBatches] = useState([
    {
      id: 1,
      name: 'Sales Follow-up',
      status: 'completed',
      progress: 100,
      successRate: 85,
      assignedAgent: 'Sales Agent',
      totalCalls: 100,
      completedCalls: 100,
      startTime: '2024-03-15 09:00',
      callScript: 'Hello, this is a follow-up call regarding your recent inquiry...',
      retryOnFailure: true,
    },
    {
      id: 2,
      name: 'Customer Survey',
      status: 'in_progress',
      progress: 45,
      successRate: 92,
      assignedAgent: 'Survey Agent',
      totalCalls: 200,
      completedCalls: 90,
      startTime: '2024-03-15 10:30',
      callScript: 'We would like to get your feedback on our recent service...',
      retryOnFailure: true,
    },
    {
      id: 3,
      name: 'Product Demo',
      status: 'scheduled',
      progress: 0,
      successRate: 0,
      assignedAgent: 'Demo Agent',
      totalCalls: 50,
      completedCalls: 0,
      startTime: '2024-03-16 14:00',
      callScript: 'Would you be interested in a demo of our new product features?',
      retryOnFailure: false,
    },
    {
      id: 4,
      name: 'Support Check',
      status: 'failed',
      progress: 30,
      successRate: 45,
      assignedAgent: 'Support Agent',
      totalCalls: 75,
      completedCalls: 25,
      startTime: '2024-03-15 13:00',
      callScript: 'We noticed you had some issues with our service. How can we help?',
      retryOnFailure: true,
    },
  ]);

  const [formData, setFormData] = useState({
    name: '',
    fromNumber: '',
    scheduleType: 'now',
    scheduledTime: '',
    recipients: [],
    callScript: '',
    retryOnFailure: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Calculate statistics
  const totalBatches = batches.length;
  const activeBatches = batches.filter(batch => batch.status === 'in_progress').length;
  const totalPlannedCalls = batches.reduce((sum, batch) => sum + batch.totalCalls, 0);
  const completedCalls = batches.reduce((sum, batch) => sum + batch.completedCalls, 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in_progress': return 'blue';
      case 'scheduled': return 'yellow';
      case 'failed': return 'red';
      case 'paused': return 'orange';
      default: return 'gray';
    }
  };

  const handleAddBatch = () => {
    setSelectedBatch(null);
    onOpen();
  };

  const handleEditBatch = (batch) => {
    setSelectedBatch(batch);
    onOpen();
  };

  const handleDeleteBatch = (id) => {
    setBatches(batches.filter(batch => batch.id !== id));
    toast({
      title: 'Batch Deleted',
      description: 'The batch has been deleted successfully.',
      status: 'success',
      duration: 3000,
    });
  };

  const handleBatchAction = (id, action) => {
    const batch = batches.find(b => b.id === id);
    if (!batch) return;

    let newStatus = batch.status;
    let message = '';

    switch (action) {
      case 'start':
        newStatus = 'in_progress';
        message = 'Batch started successfully';
        break;
      case 'pause':
        newStatus = 'paused';
        message = 'Batch paused successfully';
        break;
      case 'stop':
        newStatus = 'failed';
        message = 'Batch stopped successfully';
        break;
      default:
        return;
    }

    setBatches(batches.map(b =>
      b.id === id ? { ...b, status: newStatus } : b
    ));

    toast({
      title: 'Batch Updated',
      description: message,
      status: 'success',
      duration: 3000,
    });
  };

  const filteredBatches = batches.filter(batch =>
    batch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    batch.assignedAgent.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        toast({
          title: 'File too large',
          description: 'Please upload a file smaller than 50MB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Simulate file processing
      setIsLoading(true);
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsLoading(false);
            // Simulate parsing CSV and adding recipients
            const dummyRecipients = [
              { id: 1, phoneNumber: '+1 (555) 123-4567', status: 'pending' },
              { id: 2, phoneNumber: '+1 (555) 234-5678', status: 'pending' },
              { id: 3, phoneNumber: '+1 (555) 345-6789', status: 'pending' },
            ];
            setFormData(prev => ({ ...prev, recipients: dummyRecipients }));
            toast({
              title: 'File uploaded successfully',
              description: `${dummyRecipients.length} recipients added`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    }
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.recipients.length === 0) {
      toast({
        title: 'No recipients',
        description: 'Please upload recipients first',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({
        title: 'Batch call created',
        description: 'Your batch call has been successfully scheduled.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setFormData({
        name: '',
        fromNumber: '',
        scheduleType: 'now',
        scheduledTime: '',
        callScript: '',
        retryOnFailure: true,
        recipients: [],
      });
      setUploadProgress(0);
    } catch (error) {
      toast({
        title: 'Error creating batch call',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveDraft = () => {
    // Save current form state as draft
    toast({
      title: 'Draft saved',
      description: 'Your batch call has been saved as a draft.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  const downloadTemplate = () => {
    // Create and download CSV template
    const template = 'phone_number\n+1 (555) 123-4567\n+1 (555) 234-5678';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch_call_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box maxW="7xl" mx="auto" p={6}>
      <Card bg={bgColor} boxShadow="xl" borderRadius="xl" overflow="hidden">
        <CardBody p={8}>
          <VStack spacing={8} align="stretch">
            {/* Header Section */}
            <Flex justify="space-between" align="center">
              <HStack spacing={3}>
                <Icon as={FiPhone} boxSize={6} color={accentColor} />
                <Heading size="lg" color={headingColor}>Batch Calls</Heading>
              </HStack>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                size="md"
                onClick={onOpen}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                transition="all 0.2s"
              >
                New Batch Call
              </Button>
            </Flex>

            <Divider borderColor={borderColor} />

            {/* Stats Section */}
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
              <Card bg={cardBg} p={4} borderRadius="lg" borderWidth={1} borderColor={borderColor} _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }} transition="all 0.2s">
                <Stat>
                  <StatLabel color={textColor}>Total Batches</StatLabel>
                  <StatNumber color={headingColor}>{totalBatches}</StatNumber>
                  <StatHelpText>
                    <StatArrow type="increase" />
                    23.36%
                  </StatHelpText>
                </Stat>
              </Card>
              <Card bg={cardBg} p={4} borderRadius="lg" borderWidth={1} borderColor={borderColor} _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }} transition="all 0.2s">
                <Stat>
                  <StatLabel color={textColor}>Active Batches</StatLabel>
                  <StatNumber color={headingColor}>{activeBatches}</StatNumber>
                  <StatHelpText>Currently running</StatHelpText>
                </Stat>
              </Card>
              <Card bg={cardBg} p={4} borderRadius="lg" borderWidth={1} borderColor={borderColor} _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }} transition="all 0.2s">
                <Stat>
                  <StatLabel color={textColor}>Total Planned Calls</StatLabel>
                  <StatNumber color={headingColor}>{totalPlannedCalls}</StatNumber>
                  <StatHelpText>Across all batches</StatHelpText>
                </Stat>
              </Card>
              <Card bg={cardBg} p={4} borderRadius="lg" borderWidth={1} borderColor={borderColor} _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }} transition="all 0.2s">
                <Stat>
                  <StatLabel color={textColor}>Completed Calls</StatLabel>
                  <StatNumber color={headingColor}>{completedCalls}</StatNumber>
                  <StatHelpText>
                    <StatArrow type="increase" />
                    12.5%
                  </StatHelpText>
                </Stat>
              </Card>
            </SimpleGrid>

            {/* Create Batch Call Form */}
            <Card bg={cardBg} p={6} borderRadius="lg" borderWidth={1} borderColor={borderColor}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={6}>
                  <FormControl isRequired>
                    <FormLabel color={headingColor}>Batch Call Name</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter batch call name"
                      bg={bgColor}
                      borderColor={borderColor}
                      _hover={{ borderColor: accentColor }}
                      _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={headingColor}>From Number</FormLabel>
                    <Select
                      value={formData.fromNumber}
                      onChange={(e) => setFormData({ ...formData, fromNumber: e.target.value })}
                      placeholder="Select a phone number"
                      bg={bgColor}
                      borderColor={borderColor}
                      _hover={{ borderColor: accentColor }}
                      _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                    >
                      <option value="+1 (555) 123-4567">+1 (555) 123-4567</option>
                      <option value="+1 (555) 234-5678">+1 (555) 234-5678</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel color={headingColor}>Upload Recipients</FormLabel>
                    <Box
                      {...getRootProps()}
                      p={10}
                      border="2px dashed"
                      borderColor={isDragActive ? accentColor : borderColor}
                      borderRadius="lg"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ borderColor: accentColor }}
                      transition="all 0.2s"
                    >
                      <input {...getInputProps()} />
                      <VStack spacing={4}>
                        <Icon as={FiUpload} w={10} h={10} color={textColor} />
                        <Text color={headingColor}>
                          {isDragActive
                            ? 'Drop the file here'
                            : 'Choose a CSV or drag & drop it here'}
                        </Text>
                        <Text fontSize="sm" color={textColor}>
                          Up to 50 MB
                        </Text>
                      </VStack>
                    </Box>
                    {uploadProgress > 0 && (
                      <Progress value={uploadProgress} mt={4} colorScheme="blue" borderRadius="full" />
                    )}
                  </FormControl>

                  <Button
                    leftIcon={<FiDownload />}
                    variant="outline"
                    onClick={downloadTemplate}
                    w="full"
                    _hover={{ bg: hoverBg }}
                  >
                    Download Template
                  </Button>

                  <FormControl>
                    <FormLabel color={headingColor}>When to send the calls</FormLabel>
                    <RadioGroup
                      value={formData.scheduleType}
                      onChange={(value) => setFormData({ ...formData, scheduleType: value })}
                    >
                      <Stack direction="row" spacing={4}>
                        <Radio value="now" colorScheme="blue">Send Now</Radio>
                        <Radio value="schedule" colorScheme="blue">Schedule</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>

                  {formData.scheduleType === 'schedule' && (
                    <FormControl isRequired>
                      <FormLabel color={headingColor}>Scheduled Time</FormLabel>
                      <Input
                        type="datetime-local"
                        value={formData.scheduledTime}
                        onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                        bg={bgColor}
                        borderColor={borderColor}
                        _hover={{ borderColor: accentColor }}
                        _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                      />
                    </FormControl>
                  )}

                  <FormControl>
                    <FormLabel color={headingColor}>Call Script</FormLabel>
                    <Textarea
                      value={formData.callScript}
                      onChange={(e) => setFormData({ ...formData, callScript: e.target.value })}
                      placeholder="Enter your call script here..."
                      rows={4}
                      bg={bgColor}
                      borderColor={borderColor}
                      _hover={{ borderColor: accentColor }}
                      _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" color={headingColor}>Retry on Failure</FormLabel>
                    <Switch
                      isChecked={formData.retryOnFailure}
                      onChange={(e) => setFormData({ ...formData, retryOnFailure: e.target.checked })}
                      colorScheme="blue"
                    />
                  </FormControl>

                  {formData.recipients.length > 0 && (
                    <Card bg={bgColor} p={4} borderRadius="lg" borderWidth={1} borderColor={borderColor}>
                      <VStack align="stretch" spacing={4}>
                        <Heading size="sm" color={headingColor}>Recipients</Heading>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th color={textColor}>Phone Number</Th>
                              <Th color={textColor}>Status</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {formData.recipients.map((recipient) => (
                              <Tr key={recipient.id}>
                                <Td color={headingColor}>{recipient.phoneNumber}</Td>
                                <Td>
                                  <Badge colorScheme="yellow">{recipient.status}</Badge>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </VStack>
                    </Card>
                  )}

                  <HStack spacing={4} w="full">
                    <Button
                      leftIcon={<FiSave />}
                      onClick={handleSaveDraft}
                      variant="outline"
                      flex={1}
                      _hover={{ bg: hoverBg }}
                    >
                      Save as Draft
                    </Button>
                    <Button
                      type="submit"
                      colorScheme="blue"
                      isLoading={isLoading}
                      flex={1}
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                      transition="all 0.2s"
                      isDisabled={formData.recipients.length === 0}
                    >
                      Send
                    </Button>
                  </HStack>
                </VStack>
              </form>
            </Card>

            {/* Active Batches List */}
            <Card bg={cardBg} p={6} borderRadius="lg" borderWidth={1} borderColor={borderColor}>
              <Heading size="md" mb={4} color={headingColor}>Active Batches</Heading>
              <VStack spacing={4} align="stretch">
                {batches.map((batch) => (
                  <Card
                    key={batch.id}
                    bg={bgColor}
                    p={4}
                    borderRadius="lg"
                    borderWidth={1}
                    borderColor={borderColor}
                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                    transition="all 0.2s"
                  >
                    <VStack align="stretch" spacing={3}>
                      <HStack justify="space-between">
                        <Text fontWeight="bold" color={headingColor}>{batch.name}</Text>
                        <Badge colorScheme={getStatusColor(batch.status)} px={2} py={1} borderRadius="full">
                          {batch.status}
                        </Badge>
                      </HStack>
                      <Progress
                        value={batch.progress}
                        colorScheme={getStatusColor(batch.status)}
                        borderRadius="full"
                        size="sm"
                      />
                      <HStack justify="space-between" color={textColor}>
                        <Text fontSize="sm">Progress: {batch.progress}%</Text>
                        <Text fontSize="sm">Success Rate: {batch.successRate}%</Text>
                      </HStack>
                      <HStack justify="space-between" color={textColor}>
                        <HStack>
                          <Icon as={FiUser} />
                          <Text fontSize="sm">{batch.assignedAgent}</Text>
                        </HStack>
                        <HStack>
                          <Icon as={FiClock} />
                          <Text fontSize="sm">{batch.startTime}</Text>
                        </HStack>
                      </HStack>
                    </VStack>
                  </Card>
                ))}
              </VStack>
            </Card>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default BatchCall;